<script setup lang="ts">
import { computed } from 'vue';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import { LoadingStatus } from '@/corePagesModule/components/ezTable/types';
import { OrderTemplateSummaryEntity } from '../domains/entities/orderTemplate';
import { i18n } from '@/i18n';

const t = i18n.t;

/**
 * テンプレート一覧カードの入力パラメータ
 */
type Props = {
  /** テーブルタイトル */
  title: string;
  /** 表示対象のテンプレート一覧 */
  templates: OrderTemplateSummaryEntity[];
  /** ローディング状態 */
  loading: boolean;
  /** エラー */
  errorMessage?: string | null;
  /** ゼロ件時のメッセージ */
  emptyMessage?: string;
  /** 処理中テンプレートID一覧 */
  processingTemplateIds?: string[];
};

const props = withDefaults(defineProps<Props>(), {
  errorMessage: null,
  emptyMessage: undefined,
  processingTemplateIds:
    /**
     * 処理中テンプレートIDのデフォルト値を返す
     * @returns 空配列
     */
    (): string[] => [],
});

const resolvedEmptyMessage = computed(() => props.emptyMessage ?? (t('登録された書式はありません') as string));

const columns: string[] = ['120px', '500px', '200px', '200px', 'auto'];

/**
 * 削除ボタン押下イベントを親へ伝える
 */
const emits = defineEmits<{
  /** 削除ボタン押下時 */
  (e: 'delete-click', templateId: string): void;
}>();

/**
 * テーブル表示状態を計算する
 */
const loadingStatus = computed<LoadingStatus>(() => {
  if (props.loading) {
    return { type: 'loading' };
  }
  if (props.errorMessage) {
    return { type: 'error', errorMessage: props.errorMessage };
  }
  if (props.templates.length === 0) {
    return { type: 'zero-match' };
  }
  return { type: 'success' };
});

/**
 * UNIXタイムスタンプを日付文字列（YYYY/MM/DD）に整形する
 * @param timestamp - UNIXタイム（秒またはミリ秒）
 */
const formatDate = (timestamp: number): string => {
  const isSeconds = timestamp < 1_000_000_000_000;
  const date = new Date(isSeconds ? timestamp * 1000 : timestamp);
  return date.toLocaleDateString('ja-JP');
};

/**
 * UNIXタイムスタンプを日時文字列（YYYY/MM/DD HH:mm）に整形する
 * @param timestamp - UNIXタイム（秒またはミリ秒）
 */
const formatDateTime = (timestamp: number): string => {
  const isSeconds = timestamp < 1_000_000_000_000;
  const date = new Date(isSeconds ? timestamp * 1000 : timestamp);
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

/**
 * 指定テンプレートが処理中か判定する
 * @param templateId - 判定対象のテンプレートID
 */
const isProcessing = (templateId: string): boolean => {
  return props.processingTemplateIds.includes(templateId);
};

/**
 * 編集リンクの遷移先を構築する
 * @param template - 表示中のテンプレート概要
 */
const buildEditRoute = (template: OrderTemplateSummaryEntity) => {
  return {
    name: 'order-template-edit',
    query: {
      templateId: template.templateId,
    },
  } as const;
};

/**
 * 削除ボタン押下時のハンドラ
 * @param templateId - 削除対象のテンプレートID
 */
const handleDeleteClick = (templateId: string) => {
  emits('delete-click', templateId);
};
</script>

<template>
  <section class="template-table-card">
    <header class="template-table-card__header">
      <h2 class="template-table-card__title">{{ title }}</h2>
    </header>
    <div class="template-table-card__body">
      <EzTable :grid-template-columns="columns" :loading-status="loadingStatus">
        <template #header>
          <EzHeader>{{ t('登録日') }}</EzHeader>
          <EzHeader>{{ t('ファイル名') }}</EzHeader>
          <EzHeader>{{ t('最終更新日時') }}</EzHeader>
          <EzHeader>{{ t('最終更新者') }}</EzHeader>
          <EzHeader />
        </template>
        <template v-if="loadingStatus.type === 'success'" #body>
          <EzTableRow v-for="template in templates" :key="template.templateId" :grid-template-columns="columns">
            <EzCell>{{ formatDate(template.createAt) }}</EzCell>
            <EzCell>{{ template.fileOriginalName }}</EzCell>
            <EzCell>{{ formatDateTime(template.updateAt) }}</EzCell>
            <EzCell>{{ template.updatedByName ?? template.updatedBy }}</EzCell>
            <EzCell force-center>
              <div class="template-table-card__actions">
                <RouterLink :to="buildEditRoute(template)" class="template-table-card__button template-table-card__button--edit">
                  {{ t('編集') }}
                </RouterLink>
                <button
                  type="button"
                  class="template-table-card__button template-table-card__button--delete"
                  :disabled="isProcessing(template.templateId)"
                  @click="handleDeleteClick(template.templateId)"
                >
                  {{ t('削除') }}
                </button>
              </div>
            </EzCell>
          </EzTableRow>
        </template>
        <template #zero-match>
          <div class="template-table-card__empty">{{ resolvedEmptyMessage }}</div>
        </template>
        <template #error>
          <div class="template-table-card__error">
            {{ errorMessage || t('データの取得に失敗しました') }}
          </div>
        </template>
      </EzTable>
    </div>
  </section>
</template>

<style scoped lang="scss">
.template-table-card {
  display: grid;
  gap: 16px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid $borderDefault;
  background-color: $semanticWhite;

  &__header {
    display: grid;
  }

  &__title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: $textDefault;
  }

  &__body {
    display: grid;

    :deep(.ez-table) {
      border: 1px solid $borderDefault;
      border-radius: 2px;
      overflow: hidden;
      background-color: $semanticWhite;
    }

    :deep(.table__header-container) {
      background-color: $surfaceSecondary;
      width: 100%;
    }

    :deep(.table__header-row) {
      border-bottom: 1px solid $borderDefault;
      width: 100%;
    }

    :deep(.table__header__content) {
      min-width: 0;
      padding: 11px 8px;
      font-size: 14px;
      color: $textDefault;
    }

    :deep(.table__cell__content) {
      min-width: 0;
      padding: 9px 8px;
      font-size: 14px;
      line-height: 20px;
      color: $textDefault;
    }
  }

  &__empty,
  &__error {
    padding: 24px;
    text-align: center;
    color: $textSecondary;
  }

  &__error {
    color: $semanticError;
  }

  &__actions {
    display: grid;
    grid-auto-flow: column;
    gap: 8px;
    justify-content: center;
  }

  &__button {
    display: inline-grid;
    place-items: center;
    min-width: 72px;
    padding: 6px 12px;
    border-radius: 8px;
    border: 1px solid transparent;
    background-color: $semanticWhite;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease;

    &:hover {
      background-color: $surfaceSecondary;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--edit {
      border-color: $semanticPrimary;
      color: $semanticPrimary;
    }

    &--delete {
      border-color: $semanticError;
      color: $semanticError;
    }
  }
}
</style>
